import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

interface DragAndDropUploaderForCvProps {
  onFileUpdate: (files: File[]) => void;
  uploadedFileName?: string | null;
  disabled?: boolean;
}

export const DragAndDropUploaderForCv: React.FC<
  DragAndDropUploaderForCvProps
> = ({ onFileUpdate, uploadedFileName, disabled }) => {
  const { t } = useTranslation("cvEditor");
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // --- NO CHANGES NEEDED TO EVENT HANDLERS ---
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileUpdate(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (e.target.files && e.target.files[0]) {
      onFileUpdate(Array.from(e.target.files));
    }
  };

  const onLabelClick = () => {
    // We make the whole box clickable
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  // --- START OF THE REFACTORED RENDER LOGIC ---
  return (
    <div
      className="w-full max-w-md text-center relative"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        accept=".pdf"
        disabled={disabled}
      />

      {/* This label is now the single, consistent drop zone */}
      <div
        onClick={onLabelClick}
        className={`h-full flex flex-col w-full justify-center items-center content-center border-2 border-dashed py-8 rounded-lg transition-colors duration-300
          ${
            disabled
              ? "bg-gray-100 cursor-not-allowed"
              : "cursor-pointer border-gray-300 hover:border-blue-700"
          }
          ${dragActive ? "bg-blue-50 border-blue-700" : ""}
          ${uploadedFileName && !dragActive ? "border-blue-400 bg-blue-50" : ""}
        `}
      >
        {/* Conditional Content: Show file info or upload prompt */}
        {uploadedFileName && !dragActive ? (
          // STATE 1: File is uploaded
          <>
            <InsertDriveFileIcon className="w-10 h-10 text-blue-600" />
            <p className="font-medium text-gray-800 mt-2 text-sm break-all px-4">
              {uploadedFileName}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t(
                "upload.dragOrClickToChange",
                "Drag a new file here or click to change"
              )}
            </p>
          </>
        ) : (
          // STATE 2: No file or user is dragging a new one
          <>
            <CloudUploadIcon
              className={`w-12 h-12 text-gray-400 transition-transform duration-200 ${
                dragActive ? "transform -translate-y-1" : ""
              }`}
            />
            <p className="font-medium text-gray-700 mt-2">
              {dragActive
                ? t("upload.dropHere", "Drop your file here")
                : t("upload.drag-and-drop")}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t("upload.pdfOnly2mb", "PDF only, up to 2MB")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
